import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { 
    Mail, 
    CheckCircle2, 
    XCircle, 
    Clock, 
    RefreshCw,
    ChevronLeft,
    ChevronRight,
    AlertTriangle,
    Send,
    User,
    Calendar,
    FileText
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const EmailLogsDashboard = ({ token }) => {
    const [logs, setLogs] = useState([]);
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(0);
    const [hasMore, setHasMore] = useState(false);
    const [filter, setFilter] = useState({ status: null, event_type: null });
    const LIMIT = 20;

    const fetchEmailLogs = useCallback(async () => {
        try {
            setLoading(true);
            const params = new URLSearchParams({
                limit: LIMIT,
                skip: page * LIMIT
            });
            if (filter.status) params.append('status', filter.status);
            if (filter.event_type) params.append('event_type', filter.event_type);

            const response = await axios.get(`${API}/api/admin/email-logs?${params}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            
            setLogs(response.data.logs);
            setStats(response.data.stats);
            setHasMore(response.data.pagination.has_more);
        } catch (error) {
            console.error('Error fetching email logs:', error);
        } finally {
            setLoading(false);
        }
    }, [token, page, filter]);

    useEffect(() => {
        fetchEmailLogs();
    }, [fetchEmailLogs]);

    const getStatusBadge = (status) => {
        switch (status) {
            case 'sent':
                return <Badge className="bg-green-100 text-green-700"><CheckCircle2 className="w-3 h-3 mr-1" />Sent</Badge>;
            case 'failed':
                return <Badge className="bg-red-100 text-red-700"><XCircle className="w-3 h-3 mr-1" />Failed</Badge>;
            case 'pending':
                return <Badge className="bg-amber-100 text-amber-700"><Clock className="w-3 h-3 mr-1" />Pending</Badge>;
            default:
                return <Badge variant="outline">{status}</Badge>;
        }
    };

    const getEventTypeBadge = (eventType) => {
        const types = {
            booking: { bg: 'bg-blue-100', text: 'text-blue-700', label: 'Booking' },
            voucher: { bg: 'bg-purple-100', text: 'text-purple-700', label: 'Voucher' },
            order: { bg: 'bg-green-100', text: 'text-green-700', label: 'Order' },
            contact: { bg: 'bg-orange-100', text: 'text-orange-700', label: 'Contact' },
            admin: { bg: 'bg-gray-100', text: 'text-gray-700', label: 'Admin' }
        };
        const type = types[eventType] || { bg: 'bg-gray-100', text: 'text-gray-700', label: eventType };
        return <Badge className={`${type.bg} ${type.text}`}>{type.label}</Badge>;
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return 'N/A';
        const date = new Date(dateStr);
        return date.toLocaleString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex justify-between items-center">
                <div>
                    <h2 className="text-2xl font-bold flex items-center gap-2">
                        <Mail className="w-6 h-6" />
                        Email Logs
                    </h2>
                    <p className="text-muted-foreground">Track all transactional emails</p>
                </div>
                <Button onClick={fetchEmailLogs} variant="outline" size="sm">
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Refresh
                </Button>
            </div>

            {/* Stats Cards */}
            {stats && (
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                    <Card>
                        <CardContent className="p-4">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                                    <Mail className="w-5 h-5 text-blue-600" />
                                </div>
                                <div>
                                    <p className="text-2xl font-bold">{stats.total}</p>
                                    <p className="text-xs text-muted-foreground">Total</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardContent className="p-4">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                                    <CheckCircle2 className="w-5 h-5 text-green-600" />
                                </div>
                                <div>
                                    <p className="text-2xl font-bold">{stats.sent}</p>
                                    <p className="text-xs text-muted-foreground">Sent</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardContent className="p-4">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                                    <XCircle className="w-5 h-5 text-red-600" />
                                </div>
                                <div>
                                    <p className="text-2xl font-bold">{stats.failed}</p>
                                    <p className="text-xs text-muted-foreground">Failed</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardContent className="p-4">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                                    <Clock className="w-5 h-5 text-amber-600" />
                                </div>
                                <div>
                                    <p className="text-2xl font-bold">{stats.pending}</p>
                                    <p className="text-xs text-muted-foreground">Pending</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                    <Card className={stats.today_count >= stats.rate_limit_warning ? 'border-amber-500' : ''}>
                        <CardContent className="p-4">
                            <div className="flex items-center gap-3">
                                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                                    stats.today_count >= stats.daily_limit ? 'bg-red-100' :
                                    stats.today_count >= stats.rate_limit_warning ? 'bg-amber-100' : 'bg-gray-100'
                                }`}>
                                    <Send className={`w-5 h-5 ${
                                        stats.today_count >= stats.daily_limit ? 'text-red-600' :
                                        stats.today_count >= stats.rate_limit_warning ? 'text-amber-600' : 'text-gray-600'
                                    }`} />
                                </div>
                                <div>
                                    <p className="text-2xl font-bold">{stats.today_count}/{stats.daily_limit}</p>
                                    <p className="text-xs text-muted-foreground">Today</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </div>
            )}

            {/* Rate Limit Warning */}
            {stats && stats.today_count >= stats.rate_limit_warning && (
                <Card className="border-amber-500 bg-amber-50">
                    <CardContent className="p-4 flex items-center gap-3">
                        <AlertTriangle className="w-5 h-5 text-amber-600" />
                        <div>
                            <p className="font-semibold text-amber-800">Approaching Daily Limit</p>
                            <p className="text-sm text-amber-700">
                                {stats.today_count} of {stats.daily_limit} daily emails used. Gmail limits may apply soon.
                            </p>
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* Filters */}
            <Card>
                <CardContent className="p-4">
                    <div className="flex flex-wrap gap-2">
                        <span className="text-sm text-muted-foreground mr-2">Filter:</span>
                        <Button 
                            variant={filter.status === null ? 'default' : 'outline'} 
                            size="sm"
                            onClick={() => setFilter(f => ({ ...f, status: null }))}
                        >
                            All Status
                        </Button>
                        <Button 
                            variant={filter.status === 'sent' ? 'default' : 'outline'} 
                            size="sm"
                            onClick={() => setFilter(f => ({ ...f, status: 'sent' }))}
                        >
                            <CheckCircle2 className="w-3 h-3 mr-1" /> Sent
                        </Button>
                        <Button 
                            variant={filter.status === 'failed' ? 'default' : 'outline'} 
                            size="sm"
                            onClick={() => setFilter(f => ({ ...f, status: 'failed' }))}
                        >
                            <XCircle className="w-3 h-3 mr-1" /> Failed
                        </Button>
                        <span className="border-l mx-2"></span>
                        <Button 
                            variant={filter.event_type === null ? 'default' : 'outline'} 
                            size="sm"
                            onClick={() => setFilter(f => ({ ...f, event_type: null }))}
                        >
                            All Types
                        </Button>
                        {['booking', 'voucher', 'order', 'contact'].map(type => (
                            <Button 
                                key={type}
                                variant={filter.event_type === type ? 'default' : 'outline'} 
                                size="sm"
                                onClick={() => setFilter(f => ({ ...f, event_type: type }))}
                            >
                                {type.charAt(0).toUpperCase() + type.slice(1)}
                            </Button>
                        ))}
                    </div>
                </CardContent>
            </Card>

            {/* Email Logs Table */}
            <Card>
                <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                        <FileText className="w-5 h-5" />
                        Recent Emails
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    {loading ? (
                        <div className="flex justify-center py-10">
                            <RefreshCw className="w-6 h-6 animate-spin text-muted-foreground" />
                        </div>
                    ) : logs.length === 0 ? (
                        <div className="text-center py-10 text-muted-foreground">
                            <Mail className="w-12 h-12 mx-auto mb-3 opacity-50" />
                            <p>No email logs found</p>
                        </div>
                    ) : (
                        <div className="space-y-3">
                            {logs.map((log) => (
                                <div 
                                    key={log.id} 
                                    className={`p-4 border rounded-lg ${
                                        log.status === 'failed' ? 'border-red-200 bg-red-50' : 'hover:bg-secondary/50'
                                    }`}
                                >
                                    <div className="flex flex-wrap items-start justify-between gap-3">
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2 mb-2">
                                                {getStatusBadge(log.status)}
                                                {getEventTypeBadge(log.event_type)}
                                            </div>
                                            <p className="font-medium truncate">{log.subject}</p>
                                            <div className="flex flex-wrap items-center gap-3 mt-2 text-sm text-muted-foreground">
                                                <span className="flex items-center gap-1">
                                                    <User className="w-3 h-3" />
                                                    {log.recipient_email}
                                                </span>
                                                <span className="flex items-center gap-1">
                                                    <Calendar className="w-3 h-3" />
                                                    {formatDate(log.created_at)}
                                                </span>
                                                {log.admin_copy_email && (
                                                    <span className="flex items-center gap-1 text-blue-600">
                                                        <Mail className="w-3 h-3" />
                                                        CC: Admin
                                                    </span>
                                                )}
                                            </div>
                                            {log.error_message && (
                                                <div className="mt-2 p-2 bg-red-100 rounded text-sm text-red-700">
                                                    <strong>Error:</strong> {log.error_message}
                                                </div>
                                            )}
                                            {log.body_preview && (
                                                <p className="mt-2 text-sm text-muted-foreground line-clamp-2">
                                                    {log.body_preview}
                                                </p>
                                            )}
                                        </div>
                                        <div className="text-right text-xs text-muted-foreground">
                                            {log.sent_at && (
                                                <p>Sent: {formatDate(log.sent_at)}</p>
                                            )}
                                            {log.retry_count > 0 && (
                                                <p>Retries: {log.retry_count}</p>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Pagination */}
                    <div className="flex justify-between items-center mt-4 pt-4 border-t">
                        <Button 
                            variant="outline" 
                            size="sm" 
                            onClick={() => setPage(p => Math.max(0, p - 1))}
                            disabled={page === 0}
                        >
                            <ChevronLeft className="w-4 h-4 mr-1" /> Previous
                        </Button>
                        <span className="text-sm text-muted-foreground">
                            Page {page + 1}
                        </span>
                        <Button 
                            variant="outline" 
                            size="sm" 
                            onClick={() => setPage(p => p + 1)}
                            disabled={!hasMore}
                        >
                            Next <ChevronRight className="w-4 h-4 ml-1" />
                        </Button>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
};

export default EmailLogsDashboard;
